import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery } from 'react-query';
import { requestsAPI, customerAPI } from '../../services/api';
import { hydratePalletsFromRequest, hydrateCartonsFromRequest } from '../../utils/asnEditHydration';
import { useAuth } from '../../contexts/AuthContext';
import ShippingForm from '../../components/forms/ShippingForm';
import IncomingInventoryForm from '../../components/forms/IncomingInventoryForm';
import CrossDockForm from '../../components/forms/CrossDockForm';
import PickPackForm from '../../components/forms/PickPackForm';
import KitBuildForm from '../../components/forms/KitBuildForm';
import ManifestEditForm from '../../components/forms/ManifestEditForm';
import StepProgress from '../../components/StepProgress';
import LoadingSpinner from '../../components/LoadingSpinner';
import { FiBox, FiTruck, FiPackage, FiTool } from 'react-icons/fi';
import { LuBox, LuTruck } from 'react-icons/lu';

const defaultServiceCapabilities = {
  incoming: true,
  inbound_project: true,
  ship: true,
  crossdock: true,
  pick_pack: true,
  kit_build: true
};
const defaultReceivingInstructions = {
  require_serial_numbers_on_receive: false,
  require_mac_addresses_on_receive: false,
  relabel_on_receive: false
};

const requestTypeToCapability = {
  incoming: 'incoming',
  inbound_project: 'inbound_project',
  ship: 'ship',
  pickup: 'ship',
  crossdock: 'crossdock',
  pick_pack: 'pick_pack',
  kit_build: 'kit_build'
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatShippingMethod = (method, fallback = 'Not specified') => {
  const labels = {
    courier: 'Courier',
    freight: 'Freight',
    pickup: 'Pickup'
  };
  return labels[method] || fallback;
};

const unwrapRequestResponse = (response) => response?.request || response?.data || response;
const getRequestType = (request) => request?.requestType || request?.request_type || '';

const buildIncomingLineItems = (values) => {
  const palletLineItems = (values.pallets || []).flatMap((pallet) => {
    if (pallet.type === 'multi') {
      return (pallet.items || []).map((item) => {
        const packagingType = item.packagingType || 'individual';
        const quantity = packagingType === 'carton'
          ? toNumber(item.cartonCount, 0) * toNumber(item.unitsPerCarton, 0)
          : toNumber(item.quantity, 0);
        const cartonDimensions = item.cartonDimensions || {};
        const itemDimensions = item.itemDimensions || item.individualDimensions || {};

        return {
          // Line identity: without it the PATCH treats every submitted
          // line as new, duplicating instead of updating (RF8 review).
          id: item.requestLineItemId || null,
          productSku: item.productSku || '',
          internalSku: item.internalSku || '',
          productName: item.productName || item.productSku || 'Unnamed Product',
          quantity,
          unitOfMeasure: item.unitOfMeasure || 'each',
          packagingType,
          cartonCount: packagingType === 'carton' ? toNumber(item.cartonCount, 0) : null,
          unitsPerCarton: packagingType === 'carton' ? toNumber(item.unitsPerCarton, 0) : null,
          cartonDimensions,
          itemDimensions,
          dimensions: packagingType === 'carton' ? cartonDimensions : itemDimensions,
          weight: packagingType === 'carton'
            ? toNumber(cartonDimensions.weight || itemDimensions.weight, 0)
            : toNumber(itemDimensions.weight || item.weight, 0),
          serialNumbers: item.serialNumbers || [],
          macAddresses: item.macAddresses || [],
          declaredValue: item.declaredValue,
          notes: item.notes || ''
        };
      });
    }

    const packagingType = pallet.packagingType || 'individual';
    const quantity = packagingType === 'carton'
      ? toNumber(pallet.cartonCount, 0) * toNumber(pallet.unitsPerCarton, 0)
      : toNumber(pallet.quantity, 0);
    const cartonDimensions = pallet.cartonDimensions || {};
    const itemDimensions = pallet.itemDimensions || pallet.individualDimensions || {};

    return [{
      id: pallet.requestLineItemId || null,
      productSku: pallet.productSku || '',
      internalSku: pallet.internalSku || '',
      productName: pallet.productName || pallet.productSku || 'Unnamed Product',
      quantity,
      unitOfMeasure: pallet.unitOfMeasure || 'each',
      packagingType,
      cartonCount: packagingType === 'carton' ? toNumber(pallet.cartonCount, 0) : null,
      unitsPerCarton: packagingType === 'carton' ? toNumber(pallet.unitsPerCarton, 0) : null,
      cartonDimensions,
      itemDimensions,
      dimensions: packagingType === 'carton' ? cartonDimensions : itemDimensions,
      weight: packagingType === 'carton'
        ? toNumber(cartonDimensions.weight || itemDimensions.weight, 0)
        : toNumber(itemDimensions.weight || pallet.weight, 0),
      serialNumbers: pallet.serialNumbers || [],
      macAddresses: pallet.macAddresses || [],
      declaredValue: pallet.declaredValue,
      notes: pallet.notes || ''
    }];
  });

  const cartonLineItems = (values.cartons || []).map((carton) => {
    const cartonDimensions = carton.cartonDimensions || {};
    const itemDimensions = carton.itemDimensions || {};

    return {
      id: carton.requestLineItemId || null,
      productSku: carton.productSku || '',
      internalSku: carton.internalSku || '',
      productName: carton.productName || carton.productSku || 'Unnamed Product',
      quantity: toNumber(carton.cartonCount, 0) * toNumber(carton.unitsPerCarton, 0),
      unitOfMeasure: carton.unitOfMeasure || 'each',
      packagingType: 'carton',
      cartonCount: toNumber(carton.cartonCount, 0),
      unitsPerCarton: toNumber(carton.unitsPerCarton, 0),
      cartonDimensions,
      itemDimensions,
      dimensions: cartonDimensions,
      weight: toNumber(cartonDimensions.weight || itemDimensions.weight, 0),
      serialNumbers: carton.serialNumbers || [],
      macAddresses: carton.macAddresses || [],
      declaredValue: carton.declaredValue,
      notes: carton.notes || ''
    };
  });

  return [...palletLineItems, ...cartonLineItems].filter((item) => item.productSku && item.productName && item.quantity > 0);
};

const CreateRequest = ({ isEditing = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { id } = useParams();
  const [selectedType, setSelectedType] = useState('');
  const [step, setStep] = useState(isEditing ? 2 : 1);
  const [submitSuccess, setSubmitSuccess] = useState(true);
  const [submitError, setSubmitError] = useState(null);
  const [asnValidationFunction, setAsnValidationFunction] = useState(null);
  const [isLoadingRequest, setIsLoadingRequest] = useState(isEditing);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const hasInitializedReceivingInstructions = useRef(false);

  // Check for pre-selected request type from URL
  const urlParams = new URLSearchParams(location.search);
  const preSelectedType = urlParams.get('type');

  // Fetch existing request when editing
  const { data: existingRequest, isLoading: isLoadingExistingRequest, error: existingRequestError } = useQuery(
    ['request', id],
    async () => unwrapRequestResponse(await requestsAPI.getRequest(id)),
    {
      enabled: isEditing && !!id,
      onSuccess: () => setIsLoadingRequest(false),
      onError: () => setIsLoadingRequest(false)
    }
  );

  const { data: customerProfileResponse, isLoading: isLoadingCapabilities } = useQuery(
    ['customerProfileServiceCapabilities'],
    () => customerAPI.getProfile(),
    {
      enabled: !isEditing
    }
  );

  const serviceCapabilities = {
    ...defaultServiceCapabilities,
    ...(customerProfileResponse?.data?.serviceCapabilities || customerProfileResponse?.data?.company?.service_capabilities || {})
  };
  const defaultReceivingInstructionsFromProfile = useMemo(() => ({
    ...defaultReceivingInstructions,
    ...(customerProfileResponse?.data?.receivingPreferences || customerProfileResponse?.data?.company?.receiving_preferences || {})
  }), [customerProfileResponse]);
  
  // Handle pre-selected request type from URL and state from navigation
  useEffect(() => {
    if (preSelectedType) {
      setSelectedType(preSelectedType);
      formik.setFieldValue('requestType', preSelectedType);
      // Keep step at 1 to show company & type selection for better UX
    }
    
    // Legacy: handle returning from old shipping options page (removed).
    // Kept for backward compatibility if someone has a stale browser state.
    if (location.state?.shipmentData && location.state?.step) {
      const { shipmentData, step: returnStep } = location.state;
      formik.setValues(shipmentData);
      setSelectedType(shipmentData.requestType);
      setStep(returnStep);
    }
  }, [preSelectedType, location.state]);

  // Pre-fill form when editing an existing request
  useEffect(() => {
    const existingRequestType = getRequestType(existingRequest);
    if (isEditing && existingRequest && existingRequestType && !formik.values.requestType) {
      setSelectedType(existingRequestType === 'ship' && (existingRequest.shippingMethod || existingRequest.shipping_method) === 'pickup'
        ? 'pickup'
        : existingRequestType);
      formik.setValues({
        ...formik.values,
        requestType: existingRequestType,
        companyId: existingRequest.company_id || user?.company_id || '',
        scheduledDate: existingRequest.scheduledDate || existingRequest.scheduled_date
          ? new Date(existingRequest.scheduledDate || existingRequest.scheduled_date)
          : null,
        lineItems: (existingRequest.RequestLineItems || []).map(rli => ({
          id: rli.id,
          InventoryItemId: rli.InventoryItemId || rli.inventoryitemid || rli.inventoryItemId || rli.InventoryItem?.id || null,
          productSku: rli.productSku,
          productName: rli.productName,
          quantity: rli.quantity,
          weight: rli.weight || 0,
          unitOfMeasure: rli.unitOfMeasure || 'each',
          packagingType: rli.packagingType || rli.packaging_type || 'individual',
          cartonCount: rli.cartonCount || rli.carton_count || null,
          unitsPerCarton: rli.units_per_carton || null,
          cartonDimensions: {
            length: rli.carton_length || '',
            width: rli.carton_width || '',
            height: rli.carton_height || '',
            weight: rli.carton_weight || ''
          },
          itemDimensions: {
            length: rli.item_length || '',
            width: rli.item_width || '',
            height: rli.item_height || '',
            weight: rli.item_weight || ''
          },
          projectName: rli.project_name || '',
          projectCode: rli.project_code || '',
          specificSerials: rli.requested_serials || [],
          specificMacs: rli.requested_macs || [],
          specificAssetIds: rli.requested_asset_ids || [],
          notes: rli.notes || ''
        })),
        specialInstructions: existingRequest.specialInstructions || '',
        destinationAddress: existingRequest.destinationAddress || { street: '', city: '', state: '', postalCode: '', country: '' },
        destinationAddressId: existingRequest.destination_address_id || '',
        sourceWarehouseId: existingRequest.source_warehouse_id || '',
        shippingMethod: existingRequest.shippingMethod || existingRequest.shipping_method || '',
        liftgateRequired: existingRequest.liftgateRequired || existingRequest.liftgate_required || false,
        whiteGlove: existingRequest.whiteGlove || existingRequest.white_glove || false,
        inside_delivery: existingRequest.inside_delivery || false,
        kitName: existingRequest.kitName || '',
        kitQuantity: existingRequest.kitQuantity || 1,
        reference_number: existingRequest.reference_number || '',
        vendor: existingRequest.vendor || '',
        receivingInstructions: {
          ...defaultReceivingInstructions,
          ...(existingRequest.receivingInstructions || existingRequest.receiving_instructions || {})
        },
        // Hydrate the pallet/carton editor shapes from the stored lines
        // (the API sends Pallets/RequestLineItems; the old lowercase
        // lookups were always empty, so ASN edits started blank and any
        // re-added pallet became a duplicate unlinked line). Each item
        // carries requestLineItemId so the PATCH updates in place.
        pallets: hydratePalletsFromRequest(existingRequest),
        cartons: hydrateCartonsFromRequest(existingRequest),
        shipmentGroups: []
      });
      setStep(2); // Go straight to the form
      setIsLoadingRequest(false);
    }
  }, [isEditing, existingRequest]);

  // Create the form with Formik
  const formik = useFormik({
    initialValues: {
      requestType: '',
      customerId: '', // This would be set from auth context in a real app
      companyId: user?.company_id || '', // Set company ID from auth context
      scheduledDate: null,
      lineItems: [],
      specialInstructions: '',
      
      // Ship request fields
      destinationAddress: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      destinationAddressId: '',
      sourceWarehouseId: '',
      shippingMethod: '',
      shippingInstructions: '',
      insuranceValue: '',
      liftgateRequired: false,
      whiteGlove: false,
      inside_delivery: false,
      shipmentGroups: [],
      estimated_weight_lbs: 0,
      estimated_volume_cuft: 0,
      
      // Incoming inventory fields
      sourceAddress: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      sourceAddressId: '',
      pallets: [],
      cartons: [],
      reference_number: '',
      vendor: '',
      receivingInstructions: defaultReceivingInstructions,
      
      // Cross-dock fields
      crossdockPallets: [],
      
      // Kit building fields
      kitName: '',
      kitQuantity: 1,
      qcChecklist: []
    },
    validationSchema: Yup.object({
      requestType: Yup.string().required('Request type is required'),
      scheduledDate: Yup.date()
        .required('Scheduled date is required')
        .nullable()
        .min(new Date(new Date().setHours(0, 0, 0, 0)), 'Scheduled date cannot be in the past'),
      lineItems: Yup.array().when('requestType', {
        is: (requestType) => ['ship', 'kit_build'].includes(requestType),
        then: (schema) => schema.min(1, 'At least one item is required'),
        otherwise: (schema) => schema
      }),
      // Validation for incoming inventory requests
      pallets: Yup.array().when('requestType', {
        is: 'incoming',
        then: (schema) => schema.test('has-items', 'At least one pallet or carton is required', function(pallets) {
          const { cartons } = this.parent;
          return (pallets && pallets.length > 0) || (cartons && cartons.length > 0);
        }),
        otherwise: (schema) => schema
      }),
      cartons: Yup.array().when('requestType', {
        is: 'incoming',
        then: (schema) => schema.test('has-items', 'At least one pallet or carton is required', function(cartons) {
          const { pallets } = this.parent;
          return (cartons && cartons.length > 0) || (pallets && pallets.length > 0);
        }),
        otherwise: (schema) => schema
      }),
      specialInstructions: Yup.string(),
      destinationAddress: Yup.object().when(['requestType', 'shippingMethod'], {
        is: (requestType, shippingMethod) => requestType === 'ship' && shippingMethod !== 'pickup',
        then: () => Yup.object({
          street: Yup.string().required('Street address is required'),
          city: Yup.string().required('City is required'),
          state: Yup.string().required('State is required'),
          postalCode: Yup.string().required('Postal code is required'),
          country: Yup.string().required('Country is required')
        }),
        otherwise: () => Yup.object().nullable()
      }),
      shippingMethod: Yup.string().when('requestType', {
        is: (requestType) => requestType === 'ship',
        then: () => Yup.string().oneOf(['courier', 'freight', 'pickup']).required('Shipping method is required'),
        otherwise: () => Yup.string().nullable()
      })
    }),
    onSubmit: (values) => {
      const normalizedValues = { ...values };
      if (values.requestType === 'incoming') {
        normalizedValues.lineItems = buildIncomingLineItems(values);
      }
      if (values.requestType === 'ship' && values.shippingMethod === 'pickup') {
        normalizedValues.destinationAddress = null;
        normalizedValues.destinationAddressId = '';
        normalizedValues.liftgateRequired = false;
        normalizedValues.whiteGlove = false;
        normalizedValues.inside_delivery = false;
      }
      createRequestMutation.mutate(normalizedValues);
    }
  });

  // Create or update the request
  const createRequestMutation = useMutation(
    (values) => isEditing ? requestsAPI.updateRequest(id, values) : requestsAPI.createRequest(values),
    {
      onMutate: () => {
        setSubmitError(null);
        setIsSubmittingRequest(true);
      },
      onSuccess: (data) => {
        setSubmitSuccess(true);
        setSubmitError(null);
        const requestId = isEditing ? id : (data?.data?.id || data?.id);
        setTimeout(() => {
          navigate(`/customer/requests/${requestId}`);
        }, 1500);
      },
      onError: (error) => {
        setSubmitSuccess(false);
        const validationErrors = error?.response?.data?.errors;
        const serverMessage = error?.response?.data?.message;
        const formattedValidation = Array.isArray(validationErrors) && validationErrors.length > 0
          ? validationErrors.map((entry) => entry.msg).join(' ')
          : null;
        setSubmitError(formattedValidation || serverMessage || error.message || 'Failed to create request. Please try again.');
        setIsSubmittingRequest(false);
      }
    }
  );

  useEffect(() => {
    if (isEditing) return;
    if (formik.values.requestType && formik.values.requestType !== 'incoming') return;
    if (!hasInitializedReceivingInstructions.current) {
      formik.setFieldValue('receivingInstructions', defaultReceivingInstructionsFromProfile, false);
      hasInitializedReceivingInstructions.current = true;
    }
  }, [defaultReceivingInstructionsFromProfile, formik.values.requestType, formik.values.receivingInstructions, isEditing]);

  // Fetch inbound fee rates for the estimated charges card on the
  // incoming review step. The query re-fires when the destination
  // warehouse changes so rates stay accurate.
  const destinationWarehouseId = formik.values.destinationWarehouseId;
  const { data: inboundFeeData } = useQuery(
    ['inboundEstimateFees', destinationWarehouseId],
    () => customerAPI.getInboundEstimateFees(destinationWarehouseId),
    {
      enabled: formik.values.requestType === 'incoming' && !!destinationWarehouseId
    }
  );

  // Derive the estimated charges from fee data + form quantities.
  const inboundEstimate = useMemo(() => {
    if (!inboundFeeData?.data) return null;

    const fees = inboundFeeData.data;
    const flatRate = inboundFeeData.flatRateStorage;

    const handlingFee = fees.find(f => f.fee_type === 'handling_inbound');
    const storageFee = fees.find(f => f.fee_type === 'storage_fee');

    // Count pallets declared on the form.
    const palletCount = (formik.values.pallets || []).length;

    // Estimate pallet-equivalents for loose cartons. Use carton dims
    // when available; fall back to 1 PE per 10 cartons as a rough
    // default when dimensions are missing.
    const cartons = formik.values.cartons || [];
    let cartonPE = 0;
    cartons.forEach(c => {
      const d = c.dimensions || c.cartonDimensions || {};
      const l = Number(d.length) || 0;
      const w = Number(d.width) || 0;
      const h = Number(d.height) || 0;
      const qty = Number(c.cartonCount) || 1;
      if (l && w && h) {
        const cuftPerCarton = (l * w * h) / 1728; // inches to cubic feet
        cartonPE += Math.ceil((qty * cuftPerCarton) / 67);
      } else {
        cartonPE += Math.ceil(qty / 10);
      }
    });

    const totalPE = palletCount + cartonPE;

    return { handlingFee, storageFee, flatRate, palletCount, cartonPE, totalPE };
  }, [inboundFeeData, formik.values.pallets, formik.values.cartons]);

  // Request type options - filter based on pre-selected type
  const getAllRequestTypes = () => [
    {
      id: 'incoming',
      name: 'ASN - Incoming Inventory',
      description: 'Send detailed inventory to our warehouse for storage',
      icon: (
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707-.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
      )
    },
    {
      id: 'inbound_project',
      name: 'Inbound Manifest',
      description: 'Submit a manifest for expected incoming inventory (simple BOM)',
      icon: (
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      id: 'ship',
      name: 'Ship Products',
      description: 'Ship products from our warehouse to your destination',
      icon: (
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      )
    },
    {
      id: 'pickup',
      name: 'Pickup',
      description: 'Customer pickup from our warehouse',
      icon: (
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      )
    },

    {
      id: 'crossdock',
      name: 'Cross-Dock',
      description: 'Temporary storage with fast outbound shipping',
      icon: (
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      )
    },
    {
      id: 'kit_build',
      name: 'Kit Building',
      description: 'Have components assembled into kits',
      icon: (
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      )
    }
  ];

  const requestTypes = (() => {
    const allTypes = getAllRequestTypes();
    const enabledTypes = allTypes.filter((type) => {
      const capabilityKey = requestTypeToCapability[type.id];
      return capabilityKey ? serviceCapabilities[capabilityKey] !== false : true;
    });
    
    // Filter based on pre-selected type
    if (preSelectedType === 'incoming') {
      return enabledTypes.filter(type => ['incoming', 'inbound_project', 'inbound_manifest', 'crossdock'].includes(type.id));
    } else if (preSelectedType === 'incoming,crossdock') {
      return enabledTypes.filter(type => ['incoming', 'inbound_project', 'inbound_manifest', 'crossdock'].includes(type.id));
    } else if (preSelectedType === 'ship') {
      return enabledTypes.filter(type => ['ship', 'pickup', 'kit_build'].includes(type.id));
    } else {
      return enabledTypes; // Show all enabled types for "All Requests" or no filter
    }
  })();
  
  // Handle request type selection
  const handleTypeSelect = (typeId) => {
    setSelectedType(typeId);
    if (typeId === 'pickup') {
      formik.setFieldValue('requestType', 'ship');
      formik.setFieldValue('shippingMethod', 'pickup');
      return;
    }

    formik.setFieldValue('requestType', typeId);
    if (typeId === 'ship' && formik.values.shippingMethod === 'pickup') {
      formik.setFieldValue('shippingMethod', '');
    }
    
    // Force re-render to update step configuration
    // The stepConfig will automatically update based on the new requestType
  };
  
    // Define step mappings by request type
  const stepsByType = {
    shipping: ['Type', 'Details', 'Review'],
    asn: ['Type', 'Details', 'Review'],
    crossdock: ['Type', 'Details', 'Review'],
    kit: ['Type', 'Details', 'Review']
  };

  // Get step configuration based on request type
  const getStepConfig = () => {
    const requestType = formik.values.requestType;
    
    // If no request type selected, show only step 1
    if (!requestType) {
      return {
        steps: [{ id: 1, name: 'Type', shortName: 'Type' }],
        totalSteps: 1
      };
    }
    
    // Map request types to step configuration keys
    const typeMapping = {
      'ship': 'shipping',
      'incoming': 'asn',
      'crossdock': 'crossdock',
      'kit_build': 'kit'
    };
    
    // Get steps for the selected request type
    const stepConfigKey = typeMapping[requestType] || 'asn';
    const stepLabels = stepsByType[stepConfigKey];
    const steps = stepLabels.map((label, index) => ({
      id: index + 1,
      name: label === 'Type' ? 'Select Request Type' : label,
      shortName: label
    }));
    
    return {
      steps,
      totalSteps: steps.length
    };
  };

  const stepConfig = getStepConfig();
  const isManifestEdit = isEditing && formik.values.requestType === 'inbound_manifest';

  // Handle step changes
  const goToNextStep = async () => {
    if (step === 1 && !formik.values.requestType) {
      formik.setFieldError('requestType', 'Please select a request type');
      return;
    }
    
    // Special handling for inbound manifest - redirect to dedicated page
    if (step === 1 && formik.values.requestType === 'inbound_project') {
      navigate('/customer/inbound-projects/new');
      return;
    }
    
    // For incoming requests, validate ASN before proceeding to review
    if (formik.values.requestType === 'incoming' && step === 2) {
      if (asnValidationFunction && typeof asnValidationFunction === 'function') {
        try {
          const validationResults = await asnValidationFunction();
          if (!validationResults.isValid) {
            // Show validation errors
            const errorMessages = validationResults.errors.map(error => error.message).join('\n');
            alert(`Please resolve the following validation issues before continuing:\n\n${errorMessages}`);
            return;
          }
        } catch (error) {
          console.error('Validation failed:', error);
          console.error('Error details:', {
            message: error.message,
            stack: error.stack,
            name: error.name
          });
          alert(`Validation failed: ${error.message || 'Unknown error'}. Please check your ASN and try again.`);
          return;
        }
      } else {
      }
    }
    
    // Ship requests now go straight from Details to Review
    // (service level is selected inline on the ShippingForm).
    if (false) {
      // Legacy shipping options redirect — removed. Kept as
      // dead code placeholder so the surrounding control flow
      // doesn't shift line numbers. Will clean up later.
      navigate('/customer/shipping/options', {
        state: {}
      });
      return;
    }
    
    setStep(step + 1);
  };

  const goToPreviousStep = () => {
    setStep(step - 1);
  };
  
  // Render form based on selected type
  const renderForm = () => {
    switch (formik.values.requestType) {
      case 'incoming':
        return (
          <IncomingInventoryForm 
            formik={formik} 
            onValidationRequest={setAsnValidationFunction}
          />
        );
      case 'ship':
        return <ShippingForm formik={formik} />;
      case 'inbound_manifest':
        return (
          <ManifestEditForm
            requestId={id}
            api={requestsAPI}
            onCancel={() => navigate(`/customer/requests/${id}`)}
            onSaved={() => navigate(`/customer/requests/${id}`)}
          />
        );
      case 'crossdock':
        return <CrossDockForm formik={formik} />;
      case 'kit_build':
        return <KitBuildForm formik={formik} />;
      default:
        return <div>Please select a request type</div>;
    }
  };

  if (isEditing && existingRequestError) {
    return (
      <div className="py-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Failed to load request. Please go back to the request detail page and try again.
          </div>
        </div>
      </div>
    );
  }

  if (isEditing && !isLoadingExistingRequest && (!existingRequest || !getRequestType(existingRequest))) {
    return (
      <div className="py-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Request could not be loaded for editing. Please go back to the request detail page and try again.
          </div>
        </div>
      </div>
    );
  }

  if (isEditing && (isLoadingRequest || isLoadingExistingRequest || !formik.values.requestType)) {
    return <LoadingSpinner size="lg" label="Loading request..." />;
  }

  if (isSubmittingRequest || createRequestMutation.isLoading) {
    return <LoadingSpinner size="lg" label={isEditing ? 'Saving request...' : 'Creating request...'} />;
  }
  
  return (
    <div className="py-6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">{isEditing ? 'Edit Request' : 'Create New Request'}</h1>
          <p className="mt-2 text-sm text-gray-700">
            Create a new service request for our 3PL services
          </p>
        </div>
        
        {/* Progress Steps */}
        {!isEditing && stepConfig.steps.length > 0 && (
          <StepProgress steps={stepConfig.steps} currentStep={step} />
        )}
        
        <form onSubmit={formik.handleSubmit}>
          {/* Step 1: Select Request Type */}
          {step === 1 && (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Select Request Type</h2>
                {isLoadingCapabilities && !isEditing ? (
                  <LoadingSpinner size="md" label="Loading available services..." />
                ) : (
                <>
                {requestTypes.length === 0 && (
                  <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    No request services are currently enabled for your company. Contact your account manager if you need access.
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {requestTypes.map((type) => (
                    <div
                      key={type.id}
                      className={`relative rounded-lg border p-4 cursor-pointer hover:bg-gray-50 focus:outline-none ${
                        selectedType === type.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'
                      }`}
                      onClick={() => handleTypeSelect(type.id)}
                    >
                      <div className="flex items-start">
                        <div className={`flex-shrink-0 ${selectedType === type.id ? 'text-indigo-600' : 'text-gray-400'}`}>
                          {type.icon}
                        </div>
                        <div className="ml-3">
                          <h3 className={`text-sm font-medium ${selectedType === type.id ? 'text-indigo-900' : 'text-gray-900'}`}>
                            {type.name}
                          </h3>
                          <p className={`mt-1 text-sm ${selectedType === type.id ? 'text-indigo-700' : 'text-gray-500'}`}>
                            {type.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {formik.errors.requestType && formik.touched.requestType && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.requestType}</p>
                )}
                </>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Request Details */}
          {step === 2 && (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  {formik.values.requestType === 'incoming' && 'Incoming Inventory Details'}
                  {formik.values.requestType === 'ship' && 'Shipping Request Details'}
                  {formik.values.requestType === 'crossdock' && 'Cross-Dock Request Details'}
                  {formik.values.requestType === 'kit_build' && 'Kit Building Request Details'}
                </h2>
                {renderForm()}
              </div>
            </div>
          )}
          
          {/* Old Step 3 (Shipping Options) removed — service level
              is now selected inline on the ShippingForm in Step 2.
              Rate shopping happens on the admin side after shipment
              creation. */}
          
          {/* Review Step - Dynamic based on request type */}
          {step === stepConfig.totalSteps && (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Review Your Request</h2>
                
                <div className="border-t border-gray-200 pt-4">
                  <dl className="divide-y divide-gray-200">
                    <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Request Type</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {formik.values.requestType === 'incoming' && 'Incoming Inventory'}
                        {formik.values.requestType === 'ship' && (formik.values.shippingMethod === 'pickup' ? 'Pickup' : 'Ship Products')}
                        {formik.values.requestType === 'pick_pack' && 'Pick & Pack'}
                        {formik.values.requestType === 'kit_build' && 'Kit Building'}
                      </dd>
                    </div>

                    {formik.values.requestType === 'ship' && (
                      <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Outbound Type</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {formik.values.shippingMethod === 'pickup' ? 'Pickup' : 'Ship'}
                        </dd>
                      </div>
                    )}
                    
                    <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">
                        {formik.values.requestType === 'ship' && formik.values.shippingMethod === 'pickup'
                          ? 'Expected Pickup Date'
                          : 'Scheduled Date'}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {formik.values.scheduledDate ? formik.values.scheduledDate.toLocaleDateString() : 'Not set'}
                      </dd>
                    </div>
                    
                    {/* Only show Items section for non-crossdock requests */}
                    {formik.values.requestType !== 'crossdock' && (
                      <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Items</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {/* Display items based on request type */}
                          {formik.values.requestType === 'incoming' ? (
                          <div className="space-y-4">
                            {/* Display Pallets */}
                            {formik.values.pallets && formik.values.pallets.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Pallets ({formik.values.pallets.length})</h4>
                                <div className="border border-gray-200 rounded-md divide-y divide-gray-200">
                                  {formik.values.pallets.map((pallet, index) => (
                                    <div key={index} className="p-3">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-sm">{pallet.label}</span>
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                          pallet.type === 'single' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                        }`}>
                                          {pallet.type === 'single' ? 'Single SKU' : 'Multi SKU'}
                                        </span>
                                      </div>
                                      
                                      {pallet.type === 'single' ? (
                                        <div className="text-sm text-gray-600">
                                          <p>{pallet.productName} ({pallet.productSku})</p>
                                          <p>Qty: {pallet.quantity} {pallet.unitOfMeasure}</p>
                                        </div>
                                      ) : (
                                        <div className="text-sm text-gray-600">
                                          <p>{pallet.items?.length || 0} different items</p>
                                          {pallet.items && pallet.items.length > 0 && (
                                            <ul className="mt-1 ml-4 space-y-1">
                                              {pallet.items.slice(0, 3).map((item, itemIndex) => (
                                                <li key={itemIndex} className="text-xs">
                                                  • {item.productName} ({item.productSku}) - 
                                                  {item.packagingType === 'carton' 
                                                    ? ` ${item.cartonCount} cartons × ${item.unitsPerCarton} ${item.unitOfMeasure}`
                                                    : ` Qty: ${item.quantity} ${item.unitOfMeasure}`
                                                  }
                                                </li>
                                              ))}
                                              {pallet.items.length > 3 && (
                                                <li className="text-xs text-gray-500">
                                                  ... and {pallet.items.length - 3} more items
                                                </li>
                                              )}
                                            </ul>
                                          )}
                                        </div>
                                      )}
                                      
                                      {(pallet.dimensions?.length || pallet.dimensions?.width || pallet.dimensions?.height || pallet.weight) && (
                                        <div className="text-xs text-gray-500 mt-1">
                                          Dimensions: {pallet.dimensions.length || 0}×{pallet.dimensions.width || 0}×{pallet.dimensions.height || 0}" 
                                          {pallet.weight && `, ${pallet.weight} lbs`}
                                        </div>
                                      )}
                                      
                                      {/* Show warehouse instructions */}
                                      {(pallet.depalletizeOnArrival || pallet.crossDock) && (
                                        <div className="flex gap-2 mt-2">
                                          {pallet.depalletizeOnArrival && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                              <LuBox className="h-3.5 w-3.5 mr-1" />
                                              Depalletize
                                            </span>
                                          )}
                                          {pallet.crossDock && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                              <LuTruck className="h-3.5 w-3.5 mr-1" />
                                              Cross-dock
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Display Cartons */}
                            {formik.values.cartons && formik.values.cartons.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Cartons ({formik.values.cartons.length})</h4>
                                <div className="border border-gray-200 rounded-md divide-y divide-gray-200">
                                  {formik.values.cartons.map((carton, index) => (
                                    <div key={index} className="p-3">
                                      <div className="text-sm">
                                        <p className="font-medium">{carton.productName} ({carton.productSku})</p>
                                        <p className="text-gray-600">Qty: {carton.quantity} {carton.unitOfMeasure}</p>
                                        {(carton.dimensions?.length || carton.dimensions?.width || carton.dimensions?.height || carton.dimensions?.weight) && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            Dimensions: {carton.dimensions.length || 0}×{carton.dimensions.width || 0}×{carton.dimensions.height || 0}"
                                            {carton.dimensions.weight && `, ${carton.dimensions.weight} lbs`}
                                          </p>
                                        )}
                                        {carton.serialNumbers && carton.serialNumbers.length > 0 && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            Serial Numbers: {carton.serialNumbers.length} provided
                                          </p>
                                        )}
                                        {carton.macAddresses && carton.macAddresses.length > 0 && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            MAC Addresses: {carton.macAddresses.length} provided
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Show message if no items */}
                            {(!formik.values.pallets || formik.values.pallets.length === 0) && 
                             (!formik.values.cartons || formik.values.cartons.length === 0) && (
                              <div className="text-sm text-gray-500 italic">
                                No items added yet
                              </div>
                            )}
                          </div>
                        ) : (
                          /* For other request types, show lineItems as before */
                          <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                            {formik.values.lineItems && formik.values.lineItems.length > 0 ? (
                              formik.values.lineItems.map((item, index) => (
                                <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                                  <div className="w-0 flex-1 flex items-center">
                                    <span className="ml-2 flex-1 w-0 truncate">
                                      {item.productName} (x{item.quantity})
                                    </span>
                                  </div>
                                </li>
                              ))
                            ) : (
                              <li className="pl-3 pr-4 py-3 text-sm text-gray-500 italic">
                                No items added yet
                              </li>
                            )}
                          </ul>
                        )}
                      </dd>
                    </div>
                    )}
                    
                    {formik.values.specialInstructions && (
                      <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-gray-500">Special Instructions</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {formik.values.specialInstructions}
                        </dd>
                      </div>
                    )}

                    {formik.values.requestType === 'incoming' && Object.values(formik.values.receivingInstructions || {}).some(Boolean) && (
                      <div className="mt-6">
                        <h4 className="text-md font-medium text-gray-800 mb-3">Receiving Services Requested</h4>
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 space-y-1">
                          {formik.values.receivingInstructions.require_serial_numbers_on_receive && <div>Record serial numbers on receipt</div>}
                          {formik.values.receivingInstructions.require_mac_addresses_on_receive && <div>Record MAC addresses on receipt</div>}
                          {formik.values.receivingInstructions.relabel_on_receive && <div>Relabel units on receipt</div>}
                        </div>
                      </div>
                    )}

                    {/* Estimated charges for incoming ASN requests */}
                    {formik.values.requestType === 'incoming' && inboundEstimate && (inboundEstimate.handlingFee || inboundEstimate.storageFee || inboundEstimate.flatRate) && (
                      <div className="mt-6">
                        <h4 className="text-md font-medium text-gray-800 mb-3">Estimated Charges</h4>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm space-y-3">
                          <table className="w-full text-sm">
                            <tbody className="divide-y divide-gray-200">
                              {inboundEstimate.handlingFee && (
                                <tr>
                                  <td className="py-2 text-gray-700">Inbound Handling</td>
                                  <td className="py-2 text-gray-500 text-right whitespace-nowrap">
                                    {inboundEstimate.totalPE} {inboundEstimate.handlingFee.billing_unit === 'per_pallet' ? 'pallet' : inboundEstimate.handlingFee.billing_unit.replace('per_', '')}{inboundEstimate.totalPE !== 1 ? 's' : ''} &times; ${Number(inboundEstimate.handlingFee.fee_amount).toFixed(2)}/{inboundEstimate.handlingFee.billing_unit.replace('per_', '')}
                                  </td>
                                  <td className="py-2 text-gray-900 text-right font-medium pl-4 whitespace-nowrap">
                                    ${(inboundEstimate.totalPE * Number(inboundEstimate.handlingFee.fee_amount)).toFixed(2)}
                                  </td>
                                </tr>
                              )}
                              {inboundEstimate.flatRate ? (
                                <tr>
                                  <td className="py-2 text-gray-700">Monthly Storage</td>
                                  <td className="py-2 text-gray-500 text-right whitespace-nowrap">
                                    Flat rate
                                  </td>
                                  <td className="py-2 text-gray-900 text-right font-medium pl-4 whitespace-nowrap">
                                    ${Number(inboundEstimate.flatRate.monthly_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
                                  </td>
                                </tr>
                              ) : inboundEstimate.storageFee && (
                                <tr>
                                  <td className="py-2 text-gray-700">Monthly Storage</td>
                                  <td className="py-2 text-gray-500 text-right whitespace-nowrap">
                                    {inboundEstimate.totalPE} {inboundEstimate.storageFee.billing_unit === 'per_pallet' ? 'pallet' : inboundEstimate.storageFee.billing_unit.replace('per_', '')}{inboundEstimate.totalPE !== 1 ? 's' : ''} &times; ${Number(inboundEstimate.storageFee.fee_amount).toFixed(2)}/{inboundEstimate.storageFee.billing_unit.replace('per_', '')}
                                  </td>
                                  <td className="py-2 text-gray-900 text-right font-medium pl-4 whitespace-nowrap">
                                    ${(inboundEstimate.totalPE * Number(inboundEstimate.storageFee.fee_amount)).toFixed(2)}/mo
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                          <p className="text-xs text-gray-500 border-t border-gray-200 pt-2">
                            Final charges are calculated at receipt based on actual pallet count. Storage is billed monthly. Rates shown are your current fee schedule.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Show a note when fees are not yet configured */}
                    {formik.values.requestType === 'incoming' && destinationWarehouseId && inboundFeeData && !inboundFeeData.flatRateStorage && (!inboundFeeData.data || inboundFeeData.data.length === 0) && (
                      <div className="mt-6">
                        <h4 className="text-md font-medium text-gray-800 mb-3">Estimated Charges</h4>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500 italic">
                          Fee rates not yet configured for this warehouse. Contact your account manager for a rate quote.
                        </div>
                      </div>
                    )}

                    {/* Request-specific fields */}
                    {formik.values.requestType === 'ship' && (
                      <>
                        {formik.values.shippingMethod !== 'pickup' && (
                          <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Destination Address</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                              <address className="not-italic">
                                {formik.values.destinationAddress.street}<br />
                                {formik.values.destinationAddress.city}, {formik.values.destinationAddress.state} {formik.values.destinationAddress.postalCode}<br />
                                {formik.values.destinationAddress.country}
                              </address>
                            </dd>
                          </div>
                        )}
                        
                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                          <dt className="text-sm font-medium text-gray-500">Shipping Method</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            {formatShippingMethod(formik.values.shippingMethod)}
                          </dd>
                        </div>
                        
                        {/* Service level note */}
                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                          <dt className="text-sm font-medium text-gray-500">Shipping Cost</dt>
                          <dd className="mt-1 text-sm text-gray-500 sm:mt-0 sm:col-span-2">
                            {formik.values.shippingMethod === 'pickup'
                              ? 'No carrier rate is needed for customer pickup.'
                              : 'Best available rate will be selected for your service level.'}
                          </dd>
                        </div>
                        
                        {(formik.values.liftgateRequired || formik.values.whiteGlove) && (
                          <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Special Services</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                              {formik.values.liftgateRequired && <div>Liftgate Required</div>}
                              {formik.values.whiteGlove && <div>White Glove Service</div>}
                            </dd>
                          </div>
                        )}
                      </>
                    )}
                    
                    {formik.values.requestType === 'kit_build' && formik.values.kitName && (
                      <>
                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                          <dt className="text-sm font-medium text-gray-500">Kit Name</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            {formik.values.kitName}
                          </dd>
                        </div>
                        
                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                          <dt className="text-sm font-medium text-gray-500">Kit Quantity</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            {formik.values.kitQuantity}
                          </dd>
                        </div>
                      </>
                    )}
                    
                    {/* Cross-dock specific review section */}
                    {formik.values.requestType === 'crossdock' && (
                      <>
                        {/* Cross-dock Pallets */}
                        {formik.values.crossdockPallets && formik.values.crossdockPallets.length > 0 && (
                          <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Cross-dock Pallets</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                              <div className="space-y-3">
                                {formik.values.crossdockPallets.map((pallet, index) => (
                                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-medium text-sm">{pallet.palletLabel}</span>
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                        Qty: {pallet.palletQuantity || 1}
                                      </span>
                                    </div>
                                    <div className="text-sm text-gray-600 space-y-1">
                                      <p><span className="font-medium">Commodity:</span> {pallet.commodity}</p>
                                      <p><span className="font-medium">Dimensions:</span> {pallet.length}×{pallet.width}×{pallet.height}" ({pallet.grossWeight} lbs)</p>
                                      {pallet.notes && (
                                        <p><span className="font-medium">Notes:</span> {pallet.notes}</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                <div className="text-xs text-gray-500 mt-2">
                                  Total Pallets: {formik.values.crossdockPallets.reduce((sum, p) => sum + (parseInt(p.palletQuantity) || 1), 0)}
                                </div>
                              </div>
                            </dd>
                          </div>
                        )}
                        
                        {/* Cross-dock Configuration */}
                        {formik.values.crossdockData && (
                          <>
                            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                              <dt className="text-sm font-medium text-gray-500">Expected Dates</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <div className="space-y-1">
                                  {formik.values.crossdockData.expectedArrivalDate && (
                                    <p><span className="font-medium">Arrival:</span> {new Date(formik.values.crossdockData.expectedArrivalDate).toLocaleDateString()}</p>
                                  )}
                                  {formik.values.crossdockData.expectedPickupDate && (
                                    <p><span className="font-medium">Pickup:</span> {new Date(formik.values.crossdockData.expectedPickupDate).toLocaleDateString()}</p>
                                  )}
                                </div>
                              </dd>
                            </div>
                            
                            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                              <dt className="text-sm font-medium text-gray-500">Destination Warehouse</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                Selected warehouse
                              </dd>
                            </div>
                            
                            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                              <dt className="text-sm font-medium text-gray-500">Shipping Arrangement</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <div className="space-y-2">
                                  <p className="font-medium">
                                    {formik.values.crossdockData.shippingArrangement === 'customer' 
                                      ? 'I will arrange shipping'
                                      : 'Warehouse will arrange shipping'
                                    }
                                  </p>
                                  
                                  {/* Customer arranges - show documents */}
                                  {formik.values.crossdockData.shippingArrangement === 'customer' && (
                                    <div className="bg-blue-50 p-3 rounded-md">
                                      <p className="text-sm font-medium text-blue-800">Shipping Documents:</p>
                                      {formik.values.crossdockData.documentsProvidedNow ? (
                                        <div className="text-sm text-blue-700 mt-1 space-y-1">
                                          {formik.values.crossdockData.documents?.bolNumber && (
                                            <p>BOL: {formik.values.crossdockData.documents.bolNumber}</p>
                                          )}
                                          {formik.values.crossdockData.documents?.trackingNumber && (
                                            <p>Tracking: {formik.values.crossdockData.documents.trackingNumber}</p>
                                          )}
                                          {formik.values.crossdockData.documents?.notes && (
                                            <p>Notes: {formik.values.crossdockData.documents.notes}</p>
                                          )}
                                        </div>
                                      ) : (
                                        <p className="text-sm text-blue-700 mt-1">Will provide documents later</p>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Warehouse arranges - show destination and requirements */}
                                  {formik.values.crossdockData.shippingArrangement === 'warehouse' && (
                                    <div className="bg-green-50 p-3 rounded-md space-y-2">
                                      <p className="text-sm font-medium text-green-800">Final Destination:</p>
                                                                             {formik.values.crossdockData.crossDockDestinationType === 'existing' && formik.values.crossdockData.selectedDestinationDetails ? (
                                         <div className="text-sm text-green-700">
                                           <p>{formik.values.crossdockData.selectedDestinationDetails.name}</p>
                                           <p>{formik.values.crossdockData.selectedDestinationDetails.street_address}</p>
                                           {formik.values.crossdockData.selectedDestinationDetails.address_line_2 && (
                                             <p>{formik.values.crossdockData.selectedDestinationDetails.address_line_2}</p>
                                           )}
                                           <p>{formik.values.crossdockData.selectedDestinationDetails.city}, {formik.values.crossdockData.selectedDestinationDetails.state_province} {formik.values.crossdockData.selectedDestinationDetails.postal_code}</p>
                                           <p>{formik.values.crossdockData.selectedDestinationDetails.country}</p>
                                           {formik.values.crossdockData.selectedDestinationDetails.contact_name && (
                                             <p>Contact: {formik.values.crossdockData.selectedDestinationDetails.contact_name}</p>
                                           )}
                                           {formik.values.crossdockData.selectedDestinationDetails.contact_phone && (
                                             <p>Phone: {formik.values.crossdockData.selectedDestinationDetails.contact_phone}</p>
                                           )}
                                         </div>
                                       ) : formik.values.crossdockData.crossDockDestination && (
                                        <div className="text-sm text-green-700">
                                          <p>{formik.values.crossdockData.crossDockDestination.name}</p>
                                          <p>{formik.values.crossdockData.crossDockDestination.street_address}</p>
                                          {formik.values.crossdockData.crossDockDestination.address_line_2 && (
                                            <p>{formik.values.crossdockData.crossDockDestination.address_line_2}</p>
                                          )}
                                          <p>{formik.values.crossdockData.crossDockDestination.city}, {formik.values.crossdockData.crossDockDestination.state_province} {formik.values.crossdockData.crossDockDestination.postal_code}</p>
                                          <p>{formik.values.crossdockData.crossDockDestination.country}</p>
                                          {formik.values.crossdockData.crossDockDestination.contact_name && (
                                            <p>Contact: {formik.values.crossdockData.crossDockDestination.contact_name}</p>
                                          )}
                                          {formik.values.crossdockData.crossDockDestination.contact_phone && (
                                            <p>Phone: {formik.values.crossdockData.crossDockDestination.contact_phone}</p>
                                          )}
                                        </div>
                                      )}
                                      
                                      {formik.values.crossdockData.facilityType && (
                                        <p className="text-sm text-green-700">
                                          <span className="font-medium">Facility Type:</span> {formik.values.crossdockData.facilityType.charAt(0).toUpperCase() + formik.values.crossdockData.facilityType.slice(1)}
                                        </p>
                                      )}
                                      
                                      {(formik.values.crossdockData.siteContactName || formik.values.crossdockData.siteContactPhone) && (
                                        <div className="text-sm text-green-700">
                                          <p className="font-medium">Site Contact:</p>
                                          {formik.values.crossdockData.siteContactName && (
                                            <p>Name: {formik.values.crossdockData.siteContactName}</p>
                                          )}
                                          {formik.values.crossdockData.siteContactPhone && (
                                            <p>Phone: {formik.values.crossdockData.siteContactPhone}</p>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Delivery Requirements */}
                                      {formik.values.crossdockData.deliveryRequirements && Object.values(formik.values.crossdockData.deliveryRequirements).some(req => req) && (
                                        <div className="text-sm text-green-700">
                                          <p className="font-medium">Delivery Requirements:</p>
                                          <div className="flex flex-wrap gap-2 mt-1">
                                            {formik.values.crossdockData.deliveryRequirements.liftgateRequired && (
                                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                Liftgate Required
                                              </span>
                                            )}
                                            {formik.values.crossdockData.deliveryRequirements.whiteGloveService && (
                                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                White Glove Service
                                              </span>
                                            )}
                                            {formik.values.crossdockData.deliveryRequirements.insideDelivery && (
                                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                Inside Delivery
                                              </span>
                                            )}
                                            {formik.values.crossdockData.deliveryRequirements.appointmentRequired && (
                                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                Appointment Required
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </dd>
                            </div>
                            
                            {formik.values.crossdockData.specialInstructions && (
                              <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                                <dt className="text-sm font-medium text-gray-500">Special Instructions</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                  <div className="bg-yellow-50 p-3 rounded-md">
                                    <p className="text-sm text-yellow-800">{formik.values.crossdockData.specialInstructions}</p>
                                  </div>
                                </dd>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </dl>
                </div>
              </div>
            </div>
          )}
          
          {/* Form Navigation */}
          {!isManifestEdit && (
            <div className="mt-8 flex justify-between">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={goToPreviousStep}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Back
                </button>
              ) : (
                <div></div> // Empty div to maintain layout
              )}

              {step < stepConfig.totalSteps ? (
                <button
                  type="button"
                  onClick={goToNextStep}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {formik.values.requestType === 'ship' && step === 2 ? 'Next: Review' : 'Continue'}
                </button>
              ) : (
                <>
                  <button
                    type="submit"
                    disabled={createRequestMutation.isLoading}
                    onClick={(e) => {
                      if (!formik.isValid) {
                        e.preventDefault();
                        formik.setTouched({
                          requestType: true,
                          scheduledDate: true,
                          pallets: true,
                          cartons: true,
                          lineItems: true
                        });
                      }
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {createRequestMutation.isLoading ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create Request')}
                  </button>
                </>
              )}
            </div>
          )}
          
          {submitError && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              <p>{submitError}</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateRequest; 
